/**
 * The client-side CSV export: builds a CSV string from exactly the columns
 * and rows on screen. No server round trip, no export pipeline — a report's
 * rows already came from the API, this only serialises them.
 */

import type { ReportColumnDef, ReportRow } from '@/api/reports'

/** A leading character Excel/Sheets would treat as the start of a formula (CWE-1236). */
const FORMULA_TRIGGER = /^[=+\-@\t\r]/

function escapeCell(value: string | number): string {
  const text = String(value)
  // Neutralise a formula before quoting, so a value like "=SUM(A1)" opens as
  // literal text everywhere this CSV might land — including a scheduled
  // report's emailed recipients, who never asked to run a spreadsheet's macro.
  // A JS number can never itself be a formula — a negative delta like -5
  // stringifies to a leading "-" and must not be corrupted with an escape
  // meant for strings a caller supplied.
  const safe = typeof value === 'string' && FORMULA_TRIGGER.test(text) ? `'${text}` : text
  return /["\r\n,]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
}

/** RFC 4180-style CSV: CRLF line endings, quoted only where a cell needs it. */
export function buildCsv(columns: readonly ReportColumnDef[], rows: readonly ReportRow[]): string {
  const header = columns.map((column) => escapeCell(column.header))
  const lines = rows.map((row) => columns.map((column) => escapeCell(row[column.key] ?? '')))
  return [header, ...lines].map((line) => line.join(',')).join('\r\n')
}
